import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { UserSettings } from 'fakturnia-shared';
import { KsefCertificateDialogComponent, KsefCertificateDialogResult } from 'shared/dialogs/ksef-certificate-dialog/ksef-certificate-dialog.component';
import { KSeFApiService } from 'shared/services/api/ksef.api.service';
import { SettingsService } from 'shared/services/settings.service';
import { SnackbarService } from 'shared/services/snackbar.service';

@Component({
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, RouterLink],
  selector: 'app-ksef-setup-alert',
  templateUrl: './ksef-setup-alert.component.html'
})
export class KsefSetupAlertComponent implements OnInit, OnDestroy {
  
  private _dialog = inject(MatDialog);
  private _ksefApiService = inject(KSeFApiService);
  private _settingsService = inject(SettingsService);
  private _snackbarService = inject(SnackbarService);
  private _subscriptions: Subscription[] = [];
  
  public hasConnectionConfig = true; // domyślnie ukryty
  public isLoading = true;
  public enableKsefFeatures = true;
  public nip?: string;

  ngOnInit(): void {
    this._subscriptions.push(
      this._settingsService.getUserSettings().subscribe({
        next: (userSettings: UserSettings) => {
          this.enableKsefFeatures = userSettings?.settings?.general?.enableKsefFeatures ?? true;
        }
      })
    );

    if (this.enableKsefFeatures) {
      this._checkConnection();
    } else {
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
  }

  private _checkConnection(): void {
    this._ksefApiService.get()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          this.hasConnectionConfig = response.data?.hasConnectionConfig ?? false;
          this.nip = response.data?.nip;
          this.isLoading = false;
        },
        error: () => {
          this.hasConnectionConfig = false;
          this.isLoading = false;
        }
      });
  }

  /** Publiczna metoda do odświeżenia stanu alertu z zewnątrz */
  public refreshStatus(): void {
    this._checkConnection();
  }

  public openTokenDialog(): void {
    const dialogRef = this._dialog.open(KsefCertificateDialogComponent, {
      data: {
        hasCertificate: false,
        nip: this.nip
      },
      maxWidth: '1100px',
      width: '95vw',
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe((result: KsefCertificateDialogResult | undefined) => {
      if (result) {
        this._saveCertificate(result);
      }
    });
  }

  private _saveCertificate(payload: KsefCertificateDialogResult): void {
    this._ksefApiService.setCertificate(payload)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (response?.success) {
            this._snackbarService.showSnackBar('Certyfikat KSeF został zapisany.', 'success', 3000);

            this._ksefApiService.initSession()
              .pipe(take(1))
              .subscribe({
                next: () => this._checkConnection(),
                error: () => this._checkConnection()
              });
          }
        },
        error: () => {
          this._snackbarService.showSnackBar('Nie udało się zapisać certyfikatu KSeF.', 'error', 5000);
        }
      });
  }
}
